import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatTime,
  istanbulDayString,
  istanbulHourOf,
  t,
} from '@aquabox/shared';
import {
  useMe,
  useReloadRequests,
  useStaleOpenTrips,
  useTrip,
  useTripMutations,
  useTrips,
  useTripSummary,
} from '../api/queries';
import { Icon } from '../components/Icon';
import { Kpi } from '../components/Kpi';
import { StaleBadge } from '../components/StaleBadge';
import { PrintPdfButton } from '../components/PrintPdfButton';
import { useColumnWidths } from '../components/useColumnWidths';
import { useToast } from '../components/Toast';
import { canPrint, manifestPath } from '../lib/print-queue';
import { isStaleOpenTrip, PANEL_VOIDABLE_OPEN } from '../lib/stale-trip';
import { periodLabel } from '../lib/periods';
import type { Trip, TripDetail, TripStatus } from '../api/types';
import { PanelFleetCard } from './PanelFleetCard';
import { usePanelFleet } from '../api/panel-fleet';
import { ClosedEquationSummary, LiveTripCards } from './trips/LiveTripCards';

const STATUS_COLOR: Record<TripStatus, string> = {
  OPENED: 'var(--info)',
  LOADING: 'var(--warning)',
  ON_ROUTE: 'var(--primary)',
  CLOSING: 'var(--warning)',
  CLOSED: 'var(--success)',
  // Nötr gri: iptal edilen sefer bir uyarı değil, ARTIK GEÇERLİ OLMAYAN bir kayıttır. Listede
  // kalır (kural 1); rengi diğer durumlardan ayrıştığı için gizlemeye gerek yoktur.
  VOIDED: 'var(--text-secondary)',
};

/**
 * İptal edilebilen durumlar (`TRIP_TRANSITIONS`, trip-status.ts).
 *
 * `ON_ROUTE` / `CLOSING` YOK: şoför vardiyanın ortasındadır (kural 11). Kopya bir listedir,
 * asıl kapı sunucudadır; burada yalnız kullanılamayacak düğmeyi göstermemeye yarar.
 */
const VOIDABLE_STATUSES: readonly TripStatus[] = ['OPENED', 'LOADING', 'CLOSED'];

/** Sunucudaki `VARIANCE_NOTE_MIN_LENGTH` ile aynı eşik (trips.schema.ts#voidTripSchema). */
const VOID_REASON_MIN_LENGTH = 10;

const DATE_FILTERS = [
  { value: 'today', period: 'today' },
  { value: 'week', period: 'thisWeek' },
  { value: 'all', period: 'all' },
] as const;

/**
 * Seferler tablosu grid'i (§3.9): Araç · Şoför · Durum · Teslimat · Denklem · Tahsilat · Saatler.
 *
 * "Denklem" ve "Tahsilat" sütunları 1280/1440 px'de başlık metniyle çakışıyordu (30.09.2026
 * kullanıcı geri bildirimi); genişlikler büyütüldü ve `minWidth` buna göre yükseltildi.
 */
const TRIPS_GRID = '130px 1fr 110px 130px 130px 140px 140px';

/**
 * Seferler ekranı (§3.9) + sefer detay paneli (§3.10).
 *
 * Denklem ve emanet SUNUCUDAN gelir (defterden hesaplanır); panel yalnız gösterir —
 * araçtaki stok hiçbir yerde kolonda tutulmaz (CLAUDE.md kural 1).
 */
export function Trips() {
  const [params] = useSearchParams();
  const [dateFilter, setDateFilter] = useState<string>('today');
  // Genel Bakış "Kapanış durumu" kartından derin bağlantı (§3.3 C): belirli sefer seçili açılır.
  const [selectedId, setSelectedId] = useState<string | null>(() => params.get('trip'));
  // Deep-link: ?trip=<id> syncs selection when URL changes while on the page
  useEffect(() => {
    const id = params.get('trip');
    if (id) setSelectedId(id);
  }, [params]);
  const { style: colStyle, headerRef, resizer } = useColumnWidths('trips', 7);
  const list = useTrips({ dateFilter, pageSize: '100' });
  const detail = useTrip(selectedId);
  const pendingReloads = useReloadRequests('PENDING');
  // KPI'lar SUNUCUDAN gelir: listenin ilk sayfasından saymak "Tümü" filtresinde eksik
  // rakam veriyordu (Faz 2 denetimi U10).
  const summary = useTripSummary(dateFilter);

  const trips = list.data?.items ?? [];
  const today = istanbulDayString(new Date());
  const staleTrips = useStaleOpenTrips();
  const panelFleet = usePanelFleet();
  const hasPanelFleet = (panelFleet.data?.groups.length ?? 0) > 0;

  return (
    <div style={{ display: 'flex', minHeight: '100%' }}>
      <div
        style={{
          flex: 1,
          padding: '28px 32px 36px',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
        }}
      >
        {/* KPI seti §3.9: Açılan / Kapanan / Farkla kapanan / Toplam teslimat. */}
        <section className="kpi-grid">
          <Kpi label={t('trips.kpiOpened')} value={summary.data?.total ?? 0} />
          <Kpi label={t('trips.kpiClosed')} value={summary.data?.closed ?? 0} />
          <Kpi
            label={t('trips.kpiVariance')}
            value={summary.data?.closedWithVariance ?? 0}
            accent={(summary.data?.closedWithVariance ?? 0) > 0 ? 'danger' : undefined}
          />
          <Kpi label={t('trips.kpiDeliveries')} value={summary.data?.totalDeliveries ?? 0} />
          {/* Onay bekleyen ara yükleme sayısı: aşağıdaki kuyruk kartıyla aynı veri (PENDING). */}
          <Kpi
            label={t('trips.kpiPendingReload')}
            value={pendingReloads.isSuccess ? pendingReloads.data.items.length : '—'}
            accent={(pendingReloads.data?.items.length ?? 0) > 0 ? 'warning' : undefined}
          />
        </section>

        {/* Açık seferler kartları: tarih filtresinden bağımsız, 15 sn'de bir yenilenir. */}
        <LiveTripCards />

        <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {DATE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setDateFilter(f.value)}
              aria-pressed={dateFilter === f.value}
              style={{
                borderRadius: 'var(--r-pill)',
                padding: '10px 15px',
                background: dateFilter === f.value ? 'var(--primary)' : 'var(--surface)',
                border: `1px solid ${dateFilter === f.value ? 'var(--primary)' : 'var(--border)'}`,
                color: dateFilter === f.value ? '#fff' : 'var(--text-secondary)',
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              {periodLabel(f.period)}
            </button>
          ))}
        </div>

        {pendingReloads.data && pendingReloads.data.items.length > 0 ? (
          <ReloadQueue items={pendingReloads.data.items} />
        ) : null}

        {/* Panel araçları kartı (HAYAT_PANEL + ABANT_PANEL): boşsa HİÇ render olmaz; trip
            empty durumu kartın ALTINDA kısa kalır. */}
        <PanelFleetCard />

        {/* Bayat açık seferler: tarih süzgeci bunları gizler; yeni sipariş bu araçlara
            bağlanmaz (STALE_TRIP), kapatılmaları gerekir. */}
        {staleTrips.length > 0 ? (
          <section
            className="card"
            data-testid="stale-trips"
            style={{ padding: '14px 22px', borderLeft: '4px solid var(--warning)' }}
          >
            <strong style={{ fontSize: 14 }}>{t('trips.staleTitle')}</strong>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 8px' }}>
              {t('trips.staleIntro')}
            </p>
            <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {staleTrips.map((trip) => (
                <li key={trip.id}>
                  <button
                    onClick={() => setSelectedId(trip.id)}
                    style={{ font: 'inherit', textAlign: 'left', display: 'flex', gap: 10 }}
                  >
                    <StaleBadge />
                    <strong>{trip.vehicle.plate}</strong>
                    <span>{formatDate(trip.tripDate)}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {t(`tripStatus.${trip.status}`)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <div className="card" style={{ overflow: 'auto' }}>
          <div
            ref={headerRef}
            className="table-head table-head-stack"
            style={{
              display: 'grid',
              gridTemplateColumns: colStyle?.gridTemplateColumns ?? TRIPS_GRID,
              minWidth: 1040,
              padding: '0 22px',
              height: 44,
              alignItems: 'center',
              gap: 12,
            }}
          >
            <div className="th-cell">
              <span>{t('trips.colVehicle')}</span>
              {resizer(0)}
            </div>
            <div className="th-cell">
              <span>{t('trips.colDriver')}</span>
              {resizer(1)}
            </div>
            <div className="th-cell">
              <span>{t('trips.colStatus')}</span>
              {resizer(2)}
            </div>
            <div className="th-cell">
              <span style={{ textAlign: 'right' }}>{t('trips.colDeliveries')}</span>
              {resizer(3)}
            </div>
            <div className="th-cell">
              <span style={{ textAlign: 'right' }}>{t('trips.colEquation')}</span>
              {resizer(4)}
            </div>
            <div className="th-cell">
              <span style={{ textAlign: 'right' }}>{t('trips.colCollected')}</span>
              {resizer(5)}
            </div>
            <div className="th-cell">
              <span>{t('trips.colHours')}</span>
              {resizer(6)}
            </div>
          </div>

          {list.isPending ? (
            <p
              style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              {t('common.loading')}
            </p>
          ) : trips.length === 0 ? (
            <div
              style={{ padding: '52px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}
            >
              <Icon name="van" size={56} />
              <p style={{ marginTop: 14, fontWeight: 500 }}>
                {hasPanelFleet ? t('trips.emptyOursPanelAbove') : t('trips.empty')}
              </p>
            </div>
          ) : (
            <ul className="rowgrid-stack-list" style={{ listStyle: 'none', minWidth: 1040 }}>
              {trips.map((trip) => (
                <li key={trip.id}>
                  <button
                    onClick={() => setSelectedId(trip.id)}
                    className="rowgrid-stack"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: colStyle?.gridTemplateColumns ?? TRIPS_GRID,
                      alignItems: 'center',
                      gap: 12,
                      width: '100%',
                      padding: '0 22px',
                      height: 58,
                      borderTop: '1px solid var(--border)',
                      background: selectedId === trip.id ? 'var(--primary-bg)' : 'transparent',
                      textAlign: 'left',
                      font: 'inherit',
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{trip.vehicle.plate}</span>
                    <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
                      {trip.driver.user.fullName}
                    </span>
                    <span
                      className="pill"
                      style={{
                        background: STATUS_COLOR[trip.status],
                        color: '#fff',
                        fontSize: 11.5,
                        fontWeight: 700,
                        justifySelf: 'start',
                      }}
                    >
                      {t(`tripStatus.${trip.status}`)}
                      {isStaleOpenTrip(trip, today, istanbulHourOf(new Date())) ? (
                        <StaleBadge />
                      ) : null}
                    </span>
                    <span style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 600, fontSize: 13.5 }}>
                        {t('trips.deliveriesCount', { n: trip.deliveryCount })}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                        {t('trips.jugsSoldCount', {
                          n: trip.equation.lines.reduce((s, l) => s + l.sold, 0),
                        })}
                      </div>
                    </span>
                    <EquationCell trip={trip} />
                    {/* Emanet (driverFloat) tahsilat değildir ve nakit teslimden sonra 0 olur;
                        kapanmış her sefer "₺0,00" görünüyordu (Faz 2 denetimi U9).
                        İPTALE ÖZEL BİÇİM YOK: `deliveryCount` ve `collected` sunucuda zaten
                        iptal edilmemiş teslimatlardan toplanıyor (trips.service.ts
                        `NOT_VOIDED_DELIVERY`), yani iptalden sonra 0 dönüyorlar. Panelin ayrıca
                        üstünü çizmesi aynı gerçeği iki yerde anlatmak olurdu; iptal bilgisi
                        durum rozetinde duruyor. */}
                    <span style={{ textAlign: 'right', fontSize: 13.5 }}>
                      {formatMoney(trip.collected.total)}
                    </span>
                    <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {formatTime(trip.openedAt)} –{' '}
                      {trip.closedAt ? formatTime(trip.closedAt) : '…'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <ClosedEquationSummary trips={trips} />
        </div>
      </div>

      {selectedId && detail.data ? (
        // `key`: başka sefere geçilince kart SIFIRDAN kurulur. Aksi hâlde açık iptal formu ve
        // bir önceki seferin iptal özeti yeni seferin kartında kalırdı.
        <TripCard key={detail.data.id} trip={detail.data} onClose={() => setSelectedId(null)} />
      ) : null}
    </div>
  );
}

/** Mutasyon hatasını kullanıcıya gösterilecek metne çevirir; hata SESSİZ kalmaz (U11). */
function errorText(err: unknown): string {
  const message = (err as { message?: string } | null)?.message;
  return message && message.length > 0 ? message : t('error.internal');
}

function EquationCell({ trip }: { trip: Trip }) {
  if (!trip.equation.applicable) {
    return (
      <span style={{ textAlign: 'right', fontSize: 13, color: 'var(--text-secondary)' }}>—</span>
    );
  }
  const ok = !trip.equation.hasVariance;
  return (
    <span
      style={{
        textAlign: 'right',
        fontWeight: 700,
        fontSize: 13.5,
        color: ok ? 'var(--success)' : 'var(--danger)',
      }}
    >
      {ok ? t('trips.equationOk') : t('trips.equationOpen', { n: trip.equation.openCount })}
    </span>
  );
}

/** Onay bekleyen ara yüklemeler (§4.11): depo sayımını patron onaylar. */
export function ReloadQueue({
  items,
}: {
  items: NonNullable<ReturnType<typeof useReloadRequests>['data']>['items'];
}) {
  const { decideReload } = useTripMutations();
  return (
    <div
      className="card"
      style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <strong style={{ fontSize: 15 }}>{t('trips.reloadQueue')}</strong>
      {decideReload.isError ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{errorText(decideReload.error)}</p>
      ) : null}
      {items.map((r) => (
        <div
          key={r.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            paddingTop: 10,
            borderTop: '1px solid var(--border)',
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13.5 }}>{r.vehicle.plate}</span>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {r.driver.user.fullName} ·{' '}
            {r.items
              .map((i) => `${r.direction === 'ADD' ? '+' : '−'}${i.qty} ${i.product.name}`)
              .join(', ')}
          </span>
          <span style={{ flex: 1 }} />
          <button
            className="btn btn-primary"
            style={{ fontSize: 13, padding: '8px 14px' }}
            disabled={decideReload.isPending}
            onClick={() => decideReload.mutate({ id: r.id, approve: true })}
          >
            {t('trips.approve')}
          </button>
          <button
            className="btn"
            style={{ fontSize: 13, padding: '8px 14px' }}
            disabled={decideReload.isPending}
            onClick={() => {
              // Gerekçe SORULUR. Sabit bir metin göndermek şoföre yanlış sebebi bildiriyordu
              // (Faz 2 denetimi U12); reddin sebebini bilen tek kişi reddeden kişidir.
              const reason = window.prompt(t('trips.rejectReasonPrompt'));
              if (reason === null || reason.trim() === '') return;
              decideReload.mutate({ id: r.id, approve: false, rejectReason: reason.trim() });
            }}
          >
            {t('trips.reject')}
          </button>
        </div>
      ))}
    </div>
  );
}

/** Sefer detay paneli (§3.10): denklem kartı, defter satırları, km/mazot, teslimatlar. */
function TripCard({ trip, onClose }: { trip: TripDetail; onClose: () => void }) {
  const { markVarianceRead } = useTripMutations();
  const me = useMe();
  const printAllowed = canPrint(me.data?.role);
  const eq = trip.equation;
  const voided = trip.voidedAt !== null;

  return (
    <aside
      style={{
        width: 'var(--drawer-w)',
        flexShrink: 0,
        borderLeft: '1px solid var(--border)',
        background: 'var(--surface)',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '100%',
        overflow: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 22px',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <strong style={{ fontSize: 12, letterSpacing: 0.4, color: 'var(--text-secondary)' }}>
          {t('boss.tripDetail').toLocaleUpperCase('tr-TR')}
        </strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {printAllowed ? (
            <PrintPdfButton path={manifestPath(trip.id)} label={t('panel.print.manifestButton')} />
          ) : null}
          <button onClick={onClose} aria-label={t('common.cancel')} style={{ fontSize: 18 }}>
            ×
          </button>
        </div>
      </div>

      <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <h3 style={{ fontSize: 19, fontWeight: 700 }}>{trip.vehicle.plate}</h3>
          <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
            {trip.driver.user.fullName} · {formatDate(trip.tripDate)}
          </p>
        </div>

        {/* Bayat açık sefer: yeni sipariş bağlanmaz. Mevcut akış: OPENED/LOADING aşağıdaki iptal
            formuyla kapanır; ON_ROUTE/CLOSING'i şoför kapatır (kural 11, yeni mantık yok). */}
        {isStaleOpenTrip(trip, istanbulDayString(new Date()), istanbulHourOf(new Date())) ? (
          <div
            role="alert"
            data-testid="stale-trip-card"
            style={{
              border: '1px solid var(--warning)',
              borderRadius: 'var(--r-menu)',
              padding: 12,
              fontSize: 13.5,
              lineHeight: 1.5,
            }}
          >
            <p>
              <StaleBadge /> {t('trips.staleIntro')}
            </p>
            <p style={{ color: 'var(--text-secondary)' }}>
              {PANEL_VOIDABLE_OPEN.includes(trip.status)
                ? t('trips.staleVoidHint')
                : t('trips.staleDriverHint')}
            </p>
          </div>
        ) : null}

        {/* İptal edilen sefer detayda da AÇIKÇA öyle görünür. Aşağıdaki rakamlar tek elden,
            sunucudan gelir (ADR 2026-09-09 §4): denklem kapanış anında DONAR, tahsilat ve
            teslimat sayısı ise iptal edilmemiş satırlardan toplandığı için 0'a düşer. */}
        {voided ? (
          <div
            role="status"
            style={{
              background: 'var(--bg-danger-soft)',
              border: '1px solid var(--border)',
              borderRadius: 'var(--r-menu)',
              padding: 12,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <strong style={{ fontSize: 13.5, color: 'var(--danger)' }}>
              {t('tripStatus.VOIDED')} · {formatDateTime(trip.voidedAt!)}
            </strong>
            {trip.voidReason ? (
              <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>
                {t('panel.cancelReason')}: {trip.voidReason}
              </p>
            ) : null}
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              {t('trips.voidIrreversible')}
            </p>
          </div>
        ) : null}

        {/* Tür başına denklem (ADR 2026-09-20 §3). 19L her zaman; 15L yalnız harekette görünür. */}
        {eq.lines.map((line) => {
          const kindLabel =
            line.kind === 'JUG_19L' ? t('close.byKind_kind_jug19') : t('productKind.JUG_15L_GLASS');
          return (
            <Section key={line.kind} title={`${t('trips.sectionFull')} · ${kindLabel}`}>
              <Line label={t('trips.morningLoad')} value={String(line.morningLoad)} />
              {line.midReloadNet !== 0 ? (
                <Line
                  label={t('trips.midReload')}
                  value={`${line.midReloadNet > 0 ? '+' : ''}${line.midReloadNet}`}
                />
              ) : null}
              <Line label={t('trips.sold')} value={String(line.sold)} />
              {eq.applicable ? (
                <>
                  <Line label={t('trips.returnedToDepot')} value={String(line.returnedFull)} />
                  <Line
                    label={t('trips.diff')}
                    value={line.diffFull === 0 ? t('trips.equationOk') : `${line.diffFull}`}
                    accent={line.diffFull === 0 ? 'success' : 'danger'}
                  />
                </>
              ) : (
                <Line label={t('trips.onVehicleApprox')} value={`~${line.onVehicleApprox}`} />
              )}
            </Section>
          );
        })}

        <Section title={t('trips.sectionEmpty')}>
          {/* Boş damacana özeti tür başına değil — toplam alınan/dönen fark panel için bilgi
              amaçlı; her tür kendi satırında zaten diffEmpty taşıyor. */}
          {eq.lines.map((line) => {
            const kindLabel =
              line.kind === 'JUG_19L'
                ? t('close.byKind_kind_jug19')
                : t('productKind.JUG_15L_GLASS');
            return (
              <div key={`empty-${line.kind}`} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{kindLabel}</div>
                <Line label={t('trips.emptyReceived')} value={String(line.emptyReceived)} />
                {eq.applicable ? (
                  <>
                    <Line label={t('trips.returnedToDepot')} value={String(line.returnedEmpty)} />
                    <Line
                      label={t('trips.diff')}
                      value={line.diffEmpty === 0 ? t('trips.equationOk') : `${line.diffEmpty}`}
                      accent={line.diffEmpty === 0 ? 'success' : 'danger'}
                    />
                  </>
                ) : (
                  <Line label={t('trips.diff')} value={t('trips.equationPendingClose')} />
                )}
              </div>
            );
          })}
          {eq.foreign.foreignReceived > 0 ? (
            <Line label={t('trips.foreignReceived')} value={String(eq.foreign.foreignReceived)} />
          ) : null}
        </Section>

        {/* Tahsilat kırılımı (prototip §3.10). Emanet AYRI satırdır: aynı şey değildir. */}
        <Section title={t('trips.sectionCollected')}>
          <Line label={t('trips.collectedCash')} value={formatMoney(trip.collected.cash)} />
          <Line label={t('trips.collectedCard')} value={formatMoney(trip.collected.card)} />
          <Line label={t('trips.collectedOnline')} value={formatMoney(trip.collected.online)} />
          <Line label={t('trips.collectedCredit')} value={formatMoney(trip.collected.credit)} />
          <Line label={t('trips.driverFloat')} value={formatMoney(trip.driverFloat)} />
        </Section>

        {trip.varianceNote ? (
          <Section title={t('trips.driverNote')}>
            <p style={{ fontSize: 13.5, lineHeight: 1.5 }}>{trip.varianceNote}</p>
            {markVarianceRead.isError ? (
              <p style={{ fontSize: 12, color: 'var(--danger)' }}>
                {errorText(markVarianceRead.error)}
              </p>
            ) : null}
            {trip.varianceReadAt ? (
              <p style={{ fontSize: 12, color: 'var(--success)' }}>{t('trips.noteRead')}</p>
            ) : (
              <button
                className="btn btn-primary"
                style={{ fontSize: 13, padding: '8px 14px', marginTop: 6 }}
                disabled={markVarianceRead.isPending}
                onClick={() => markVarianceRead.mutate(trip.id)}
              >
                {t('trips.markNoteRead')}
              </button>
            )}
          </Section>
        ) : null}

        <Section title={t('trips.sectionKm')}>
          <Line
            label={t('trips.declaredKm')}
            value={trip.declaredKm ? `${trip.declaredKm} km` : '—'}
          />
          <Line
            label={t('trips.odometer')}
            value={
              trip.odometerStart !== null && trip.odometerEnd !== null
                ? `${trip.odometerStart} → ${trip.odometerEnd}`
                : '—'
            }
          />
          {trip.fuelLogs.map((f) => (
            <Line
              key={f.id}
              label={t('trips.fuelLiters', { liters: f.liters })}
              value={formatMoney(f.amount)}
            />
          ))}
          {trip.odometerLogs.some((o) => o.unexplained) ? (
            <p style={{ fontSize: 12.5, color: 'var(--warning)', fontWeight: 600 }}>
              {t('trips.unexplainedKm')}
            </p>
          ) : null}
        </Section>

        <Section title={t('trips.sectionDeliveries', { n: trip.deliveries.length })}>
          {trip.deliveries.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {t('trips.noDeliveries')}
            </p>
          ) : (
            // İptal edilen teslimat LİSTEDE KALIR (ters satırı yazılamaz, damgalanır — ADR §1);
            // gizlenmez: satırın rakamları çizilir ve yanına "İptal edildi" düşer. İşaretin
            // kendisi çizilmez — okunması gereken tek şey odur.
            trip.deliveries.map((d) => {
              const struck = d.voidedAt ? { textDecoration: 'line-through' as const } : null;
              return (
                <div
                  key={d.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                    ...(d.voidedAt ? { color: 'var(--text-secondary)' } : null),
                  }}
                >
                  <span style={{ fontSize: 13, flex: 1 }}>
                    <span style={{ ...struck }}>
                      {d.order.customerName}
                      {d.leftAtDoor ? ` · ${t('orderStatus.LEFT_AT_DOOR')}` : ''}
                    </span>
                    {d.voidedAt ? ` · ${t('tripStatus.VOIDED')}` : ''}
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)', ...struck }}>
                    {d.soldJugs}↓ {d.emptyReceived}↑
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 600, ...struck }}>
                    {formatMoney(d.amount)}
                  </span>
                </div>
              );
            })
          )}
        </Section>

        <VoidTripAction trip={trip} />
      </div>
    </aside>
  );
}

/**
 * Seferi iptal et (ADR 2026-09-09): silme değil, ters kayıt.
 *
 * İki kapı da atlanamaz: (1) en az {@link VOID_REASON_MIN_LENGTH} karakterlik gerekçe —
 * sunucu da doğruluyor, (2) yapılanın geri alınamayacağını söyleyen açık onay.
 *
 * Düğme yalnız `OWNER`'a ve yalnız iptal edilebilir durumdaki seferde görünür; ama YETKİNİN
 * ASIL KAPISI SUNUCUDADIR (`@Roles('OWNER')` + `RolesGuard`). Arayüzde gizlemek yetmez,
 * yalnız gürültüyü azaltır.
 */
function VoidTripAction({ trip }: { trip: TripDetail }) {
  const me = useMe();
  const toast = useToast();
  const { voidTrip } = useTripMutations();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const result = voidTrip.data?.void ?? null;
  const reasonOk = reason.trim().length >= VOID_REASON_MIN_LENGTH;

  async function submit(): Promise<void> {
    const text = reason.trim();
    if (text.length < VOID_REASON_MIN_LENGTH) return;
    // Geri alınamazlık onayı: yanlışlıkla iptal edilen sefer GERİ AÇILMAZ, düzeltme yeni bir
    // kayıtla yapılır (kural 1). Bu yüzden gerekçeden sonra ayrıca açık onay istenir.
    // Metin `trips.voidIrreversible` OLMAK ZORUNDA: `stock.ledgerNote` yalnız "kayıt silinmez"
    // diyordu, yani onaylanan şeyin geri alınamadığını söylemiyordu — tam tersini ima ediyordu.
    const confirmed = window.confirm(
      `${t('trips.voidButton')} — ${trip.vehicle.plate}\n\n${t('trips.voidIrreversible')}\n\n${t(
        'panel.cancelReason',
      )}: ${text}`,
    );
    if (!confirmed) return;
    try {
      await voidTrip.mutateAsync({ id: trip.id, reason: text });
      toast(t('trips.voidConfirm'));
      setOpen(false);
    } catch (err) {
      // Sunucunun KENDİ metni gösterilir (nakit kasaya girmiş / zaten iptal / durum uygun
      // değil); genel "bir hata oldu" ile yutulmaz. Form açık kalır ki kullanıcı okuyabilsin.
      toast(errorText(err), 'danger');
    }
  }

  // İptal sonrası ne olduğunun özeti. Rakamlar SUNUCUDAN gelir (yanıttaki `void`); panel
  // saymaz. Sefer kartı yeniden açıldığında bu blok gitmiş olur — kalıcı iz seferin kendi
  // `voidedAt`/`voidReason` alanlarında ve defterdeki ters satırlardadır.
  if (result) {
    return (
      <Section title={t('tripStatus.VOIDED')}>
        {/* Etiket+değer değil tek cümle: rakam metnin İÇİNDE ({n}) geçer. Etiketler eskiden
            başka ekranlardan ödünçtü ("Teslimat", "Teslim edilemedi", "İPTAL MAKBUZU") ve
            neyin iptal edildiğini söylemiyordu. */}
        <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          {t('trips.voidedDeliveries', { n: result.voidedDeliveries })}
        </p>
        <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          {t('trips.voidedOrders', { n: result.failedOrders })}
        </p>
        <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
          {t('trips.voidReceiptCount', { n: result.voidReceipts.length })}
        </p>
      </Section>
    );
  }

  const canVoid =
    me.data?.role === 'OWNER' && trip.voidedAt === null && VOIDABLE_STATUSES.includes(trip.status);
  if (!canVoid) return null;

  if (!open) {
    return (
      <button
        className="btn btn-danger"
        style={{ fontSize: 13, padding: '10px 16px', alignSelf: 'flex-start' }}
        onClick={() => setOpen(true)}
      >
        {t('trips.voidButton')}
      </button>
    );
  }

  return (
    <div
      style={{
        border: '1px solid var(--danger)',
        borderRadius: 'var(--r-menu)',
        background: 'var(--bg-danger-soft)',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}
    >
      <p style={{ fontSize: 13, lineHeight: 1.5 }}>{t('trips.voidPrompt')}</p>
      <textarea
        className="input"
        style={{ height: 76, padding: '10px 14px', fontSize: 14, resize: 'vertical' }}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        aria-label={t('panel.cancelReason')}
        maxLength={1000}
      />
      {!reasonOk && reason.length > 0 ? (
        <p style={{ fontSize: 12.5, color: 'var(--danger)' }}>{t('trip.voidReasonRequired')}</p>
      ) : null}
      {voidTrip.isError ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>{errorText(voidTrip.error)}</p>
      ) : null}
      <div style={{ display: 'flex', gap: 10 }}>
        <button
          className="btn"
          style={{ fontSize: 13, padding: '8px 14px', color: 'var(--text-secondary)' }}
          onClick={() => setOpen(false)}
        >
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-danger"
          style={{ fontSize: 13, padding: '8px 14px' }}
          disabled={!reasonOk || voidTrip.isPending}
          onClick={() => void submit()}
        >
          {t('trips.voidButton')}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <strong style={{ fontSize: 11.5, letterSpacing: 0.4, color: 'var(--text-secondary)' }}>
        {title.toLocaleUpperCase('tr-TR')}
      </strong>
      {children}
    </div>
  );
}

function Line({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'success' | 'danger';
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{label}</span>
      <span
        style={{
          fontSize: 13.5,
          fontWeight: 600,
          color:
            accent === 'success'
              ? 'var(--success)'
              : accent === 'danger'
                ? 'var(--danger)'
                : 'var(--text-primary)',
        }}
      >
        {value}
      </span>
    </div>
  );
}
